'use client';

import { useMemo, useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { highlightCode, type CodeTokenKind } from '@/lib/codeHighlight';
import { cn } from '@/lib/utils';

type CodeBlockProps = {
  code: string;
  language: string;
  copied?: boolean;
  onCopy?: () => void;
  defaultWrap?: boolean;
  showWrapToggle?: boolean;
  collapsible?: boolean;
  maxHeight?: string;
  className?: string;
};

export const CODE_FONT_FAMILY =
  '"JetBrains Mono", "Fira Code", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const LARGE_CODE_CHAR_LIMIT = 200_000;
const PREVIEW_CHAR_LIMIT = 20_000;
export const CODE_TOKEN_CLASS_NAMES: Record<CodeTokenKind, string> = {
  plain: '',
  comment: 'text-[#7a8797] italic',
  keyword: 'font-semibold text-[#7c3aed]',
  string: 'text-[#0f766e]',
  property: 'font-medium text-[#0d6d8a]',
  number: 'text-[#b45309]',
  operator: 'text-[#475569]',
  punctuation: 'text-[#64748b]',
  function: 'text-[#2563eb]',
  variable: 'text-[#c026d3]',
};

export function HighlightedCode({ code, language }: { code: string; language: string }) {
  const highlightedTokens = useMemo(
    () => highlightCode(code, language),
    [code, language],
  );

  return (
    <>
      {highlightedTokens.map((token, index) => (
        <span
          key={`${token.kind}-${index}`}
          className={CODE_TOKEN_CLASS_NAMES[token.kind]}
          data-token-kind={token.kind}
        >
          {token.value}
        </span>
      ))}
    </>
  );
}

export function CodeBlock({
  code,
  language,
  copied = false,
  onCopy,
  defaultWrap,
  showWrapToggle,
  collapsible,
  maxHeight,
  className,
}: CodeBlockProps) {
  const [wrap, setWrap] = useState(Boolean(defaultWrap));
  const [expanded, setExpanded] = useState(false);

  const isLarge = code.length > LARGE_CODE_CHAR_LIMIT;
  const lineCount = useMemo(() => {
    if (isLarge) return 0;
    return code.split('\n').length;
  }, [code, isLarge]);
  const canCollapse = Boolean(collapsible) && (isLarge || lineCount > 12);
  const isExpanded = expanded || !canCollapse;
  const displayedCode = useMemo(() => {
    if (isExpanded) return code;
    if (code.length <= PREVIEW_CHAR_LIMIT) return code;
    return `${code.slice(0, PREVIEW_CHAR_LIMIT)}\n…(обрезано)`;
  }, [code, isExpanded]);
  const hasToolbar = Boolean(showWrapToggle || onCopy);
  const controlClassName =
    'rounded px-2 py-1 text-xs text-[var(--site-ink)] hover:bg-[color:rgba(23,38,59,0.12)]';
  const copyButtonClassName = 'rounded p-1 hover:bg-[color:rgba(23,38,59,0.12)]';

  return (
    <div className={cn('relative', className)}>
      <div
        className="overflow-hidden rounded-lg border border-[color:var(--site-border-soft)] bg-[#fbfaf7] shadow-[0_14px_30px_rgba(23,38,59,0.06)]"
      >
        {hasToolbar ? (
          <div className="flex items-center justify-end gap-2 px-3 pt-3">
            {showWrapToggle ? (
              <button type="button" onClick={() => setWrap((value) => !value)} className={controlClassName}>
                {wrap ? 'Без переноса' : 'Перенос'}
              </button>
            ) : null}
            {onCopy ? (
              <button type="button" onClick={onCopy} className={copyButtonClassName} aria-label="Скопировать код">
                {copied ? (
                  <CheckIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <CopyIcon className="h-5 w-5 text-[var(--site-ink)]" />
                )}
              </button>
            ) : null}
          </div>
        ) : null}

        <pre
          className={cn(
            'overflow-auto bg-transparent p-4 text-xs leading-5 text-[var(--site-ink-strong)]',
            hasToolbar && 'pt-2',
            canCollapse && !isExpanded && !maxHeight && 'max-h-[280px] sm:max-h-[360px] lg:max-h-[420px]',
          )}
          style={{
            margin: 0,
            fontSize: 12,
            maxHeight: maxHeight ?? undefined,
            whiteSpace: wrap ? 'pre-wrap' : 'pre',
            wordBreak: wrap ? 'break-word' : 'normal',
          }}
        >
          <code
            style={{
              fontFamily: CODE_FONT_FAMILY,
              background: 'transparent',
            }}
            data-language={language}
          >
            <HighlightedCode code={displayedCode} language={language} />
          </code>
        </pre>
      </div>

      {canCollapse ? (
        <div className="mt-2 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setExpanded((value) => !value)}>
            {isExpanded ? 'Свернуть' : 'Развернуть'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
